import api from '../api/axios';
import { WaitlistRegistration } from '../models/waitlistRegistration';

/** A platform organisation as listed on the System Admin Organisations page. */
export interface SystemAdminOrganisation {
  id: number;
  name: string;
  slug: string;
  email: string | null;
  isActive: boolean;
}

/** The open subscription period plus the entitlement it resolves to today. */
export interface OrgSubscription {
  id: number;
  tierCode: string;
  tierName: string | null;
  status: 'trial' | 'active' | 'past_due' | 'suspended' | 'cancelled' | 'terminated';
  billingPeriod: 'monthly' | 'annual';
  startedAt: string | null;
  trialEndsAt: string | null;
  renewsAt: string | null;
  /** Children permitted, after any override. null = unbounded (bespoke band). */
  maxChildren: number | null;
  /** What the tier alone permits. null = unbounded. */
  tierMaxChildren: number | null;
  /** Extra headroom granted to this org alone. null = none, using the tier's. */
  seatOverride: number | null;
  /** Effective entitlement — the tier default unless overridden per org. */
  aiFullEnabled: boolean;
  /** True when aiFullEnabled comes from a per-org override, not the tier. */
  aiFullIsOverride: boolean;
  /** false = soft cap (we get emailed); true = new children are blocked. */
  enforceChildCap: boolean;
  currency: string;
  /** Book price for the billing period. null on the bespoke (POA) band. */
  listPrice: number | null;
  /** listPrice after any per-org discount. */
  chargedPrice: number | null;
  discountPercent: number;
  discountEndsAt: string | null;
  notes: string | null;
}

/** Child roll for an organisation. A fact about the org, not the subscription. */
export interface OrgChildCounts {
  /** Billable: on roll, not left, not soft-deleted, not archived. */
  onRoll: number;
  /** Flagged as left but not yet soft-deleted. Never billed. */
  leavers: number;
}

/** An organisation as listed with its commercial state. */
export interface SystemAdminOrgSubscription {
  id: number;
  name: string;
  slug: string;
  email: string | null;
  isActive: boolean;
  childCounts: OrgChildCounts;
  /** null when the org has no open period — shouldn't happen post-migration. */
  subscription: OrgSubscription | null;
}

/** A member of one organisation, as shown on the System Admin Access tab. */
export interface OrgMember {
  id: number;
  name: string;
  email: string;
  /** Role IDs held in THIS organisation. Admin is 1 — see Roles on the API. */
  roleIds: number[];
  /** Membership state: false = kept on record, no access to this org. */
  isActive: boolean;
  /** External members are scoped to a whitelist and can never be Admins. */
  isExternal: boolean;
}

/** One use of the break-glass Admin grant, newest first. Append-only. */
export interface BreakGlassGrant {
  id: number;
  /** The member who was promoted. */
  userName: string;
  /** Email of the system admin who reached in. */
  grantedBy: string;
  reason: string;
  /** True when the org had NO active Admin — real recovery, not a precaution. */
  wasLastResort: boolean;
  /** True when the grant also switched a disabled membership back on. */
  reactivated: boolean;
  grantedAt: string;
}

/** Members plus break-glass history for one organisation. */
export interface OrgAccess {
  users: OrgMember[];
  grants: BreakGlassGrant[];
}

/** The Admin role id. Fixed in security.role and mirrored by Roles on the API. */
export const ADMIN_ROLE_ID = 1;

/**
 * Client for the cross-org System Administrator area. Access is gated on the
 * backend by SYSTEM_ADMIN_EMAILS (email allow-list), not by an org role — so
 * these calls carry only the Cognito token (org/PIN headers are ignored).
 */
export class SystemAdminDataService {
  /**
   * Whether the currently authenticated Cognito user is a system admin.
   * Never throws for a plain non-admin — returns false. Network/auth errors
   * are surfaced so callers can distinguish "not admin" from "couldn't check".
   */
  static async checkSystemAdmin(): Promise<boolean> {
    const response = await api.get('/system-admin/me');
    return response.data?.isSystemAdmin === true;
  }

  /** All waitlist registrations, newest first. System admins only (403 otherwise). */
  static async fetchWaitlistRegistrations(): Promise<WaitlistRegistration[]> {
    const response = await api.get('/system-admin/waitlist');
    return response.data.registrations || [];
  }

  /** Every organisation on the platform, A→Z. System admins only (403 otherwise). */
  static async fetchOrganisations(): Promise<SystemAdminOrganisation[]> {
    const response = await api.get('/system-admin/organisations');
    return response.data.organisations || [];
  }

  /**
   * Every organisation with its current tier, entitlement and child roll.
   * A superset of fetchOrganisations — the Organisations list uses this one so
   * tier and capacity are visible without a second request per row.
   */
  static async fetchOrgSubscriptions(): Promise<SystemAdminOrgSubscription[]> {
    const response = await api.get('/system-admin/organisations/subscriptions');
    return response.data.organisations || [];
  }

  /**
   * Move an organisation to a tier / status / billing period. Closes the open
   * period and opens a new one — a change of contract, not an edit, so both
   * survive in the history. Discounts and entitlement overrides carry forward.
   */
  static async changeSubscription(orgId: number, payload: ChangeSubscriptionPayload): Promise<void> {
    await api.put(`/system-admin/organisations/${orgId}/subscription`, payload);
  }

  /** Edit the open period in place — overrides and commercial adjustments. */
  static async updateEntitlement(orgId: number, payload: UpdateEntitlementPayload): Promise<void> {
    await api.patch(`/system-admin/organisations/${orgId}/entitlement`, payload);
  }

  /** Everyone in one organisation, with its break-glass history. */
  static async fetchOrgAccess(orgId: number): Promise<OrgAccess> {
    const response = await api.get(`/system-admin/organisations/${orgId}/access`);
    return { users: response.data.users || [], grants: response.data.grants || [] };
  }

  /**
   * Break-glass: give an existing member the Admin role, reactivating their
   * membership if it was disabled. The only way back for an organisation left
   * with no admins — the org's own screens refuse to reach zero, but nothing
   * in the product can appoint a replacement once it happens from outside.
   *
   * The reason is required (10 chars) and recorded permanently against the
   * grant, alongside the system admin's email.
   */
  static async grantOrgAdmin(
    orgId: number,
    userId: number,
    reason: string
  ): Promise<{ name: string; reactivated: boolean; wasLastResort: boolean }> {
    const response = await api.post(`/system-admin/organisations/${orgId}/grant-admin`, { userId, reason });
    return {
      name: response.data.name,
      reactivated: response.data.reactivated === true,
      wasLastResort: response.data.wasLastResort === true,
    };
  }

  /** Every period this organisation has had, newest first. */
  static async fetchSubscriptionHistory(orgId: number): Promise<SubscriptionPeriod[]> {
    const response = await api.get(`/system-admin/organisations/${orgId}/subscription-history`);
    return response.data.periods || [];
  }

  /**
   * Create an organisation and its first admin (sign-in, invitation,
   * membership, Admin/Editor/Reporting roles, subscription) in one go.
   */
  static async createOrganisation(payload: NewOrganisationPayload): Promise<NewOrganisationResult> {
    const response = await api.post('/system-admin/organisations', payload);
    return response.data;
  }

  /** Every version of every tier, for the Pricing screen. */
  static async fetchPricing(): Promise<PricingTierVersion[]> {
    const response = await api.get('/system-admin/pricing');
    return response.data.versions || [];
  }

  /** How many organisations a tier edit would affect. */
  static async fetchTierImpact(code: string): Promise<TierImpact> {
    const response = await api.get(`/system-admin/pricing/${encodeURIComponent(code)}/impact`);
    return { organisations: response.data.organisations ?? 0, overNewLimit: response.data.overNewLimit ?? 0 };
  }

  /** Publish a tier version. Append-only — see PricingTierVersion.isDraft. */
  static async publishTierVersion(payload: PublishTierPayload): Promise<void> {
    await api.post('/system-admin/pricing', payload);
  }

  /** Discard a future-dated draft. Refused for versions in force or past. */
  static async deleteTierDraft(code: string, effectiveFrom: string): Promise<void> {
    await api.delete(`/system-admin/pricing/${encodeURIComponent(code)}/${effectiveFrom}`);
  }
}

export interface ChangeSubscriptionPayload {
  tierCode: string;
  status: OrgSubscription['status'];
  billingPeriod: OrgSubscription['billingPeriod'];
  /** YYYY-MM-DD. Closes the current period on this date and opens the next. */
  effectiveDate: string;
  notes?: string | null;
}

/**
 * Every field is optional — omit it to leave that value untouched.
 *
 * `aiFullOverride` is tri-state and says so in the type: 'inherit' clears the
 * override so the tier decides. A boolean alone could not express that, and
 * "inherit" is the state that makes a later tier upgrade actually grant the
 * feature — so it must be expressible.
 */
export interface UpdateEntitlementPayload {
  aiFullOverride?: boolean | 'inherit';
  /** null clears the override, returning the org to its tier's allowance. */
  seatOverride?: number | null;
  enforceChildCap?: boolean;
  discountPercent?: number;
  discountEndsAt?: string | null;
  notes?: string | null;
}

/** One closed or open subscription period, priced as it was at the time. */
export interface SubscriptionPeriod {
  id: number;
  tierCode: string;
  tierName: string | null;
  status: OrgSubscription['status'];
  billingPeriod: OrgSubscription['billingPeriod'];
  startedAt: string | null;
  endedAt: string | null;
  isOpen: boolean;
  /** Resolved against the book version in force when the period STARTED. */
  priceThen: number | null;
  currency: string;
  discountPercent: number;
  notes: string | null;
}

/** One dated version of one tier in the price book. */
export interface PricingTierVersion {
  code: string;
  effectiveFrom: string;
  displayName: string;
  audience: string | null;
  /** null = unbounded (the bespoke band). */
  maxChildren: number | null;
  aiInsightsIncluded: boolean;
  /** null = price on application. */
  monthlyPrice: number | null;
  annualPrice: number | null;
  currency: string;
  sortOrder: number;
  isSellable: boolean;
  notes: string | null;
  /** In force today — the version an organisation's entitlement resolves to. */
  isCurrent: boolean;
  /** Future-dated, so still editable and discardable. */
  isDraft: boolean;
}

export type PublishTierPayload = Omit<PricingTierVersion, 'isCurrent' | 'isDraft'>;

export interface TierImpact {
  organisations: number;
  overNewLimit: number;
}

/** Human labels for the subscription status values (DB CHECK constraint). */
export const SUBSCRIPTION_STATUS_LABELS: Record<OrgSubscription['status'], string> = {
  trial: 'Trial',
  active: 'Active',
  past_due: 'Past due',
  suspended: 'Suspended',
  cancelled: 'Cancelled',
  terminated: 'Terminated',
};

/**
 * How close an organisation is to its child limit, as a state rather than a
 * number — so the list can encode it in form (colour, icon) and not just text.
 * Unbounded tiers are always 'ok'.
 */
export type CapacityState = 'ok' | 'near' | 'over';

export function capacityState(onRoll: number, maxChildren: number | null): CapacityState {
  if (maxChildren == null || maxChildren <= 0) return 'ok';
  if (onRoll > maxChildren) return 'over';
  if (onRoll >= maxChildren * 0.9) return 'near';
  return 'ok';
}

/** Money for display. Prices are whole pounds today, but never assume it. */
export function formatPrice(value: number | null, currency: string): string {
  if (value == null) return 'POA';
  try {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: currency || 'GBP',
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    });
  } catch {
    // Unknown currency code — don't lose the number over a formatting nicety.
    return `${value} ${currency}`;
  }
}

export interface NewOrganisationPayload {
  name: string;
  slug: string;
  email?: string;
  tierCode: string;
  status: 'trial' | 'active';
  billingPeriod: 'monthly' | 'annual';
  admin: { name: string; email: string };
}

export interface NewOrganisationResult {
  organisationId: number;
  adminUserId: number;
  /** False when the admin already had a Rustle It account. */
  adminIsNewUser: boolean;
  /** True when Cognito emailed an invitation with a temporary password. */
  invited: boolean;
  /** True when the welcome email went out. */
  notified: boolean;
}
